import { SessionEstimateItem, SessionEstimateItemUnitType, TItemDefaultField as TResetField } from '@talon/web/lib/api';
import { EditableText, LineItemDelete, SelectableText } from '@talon/web/src/components';
import { useEstimateStore } from '@talon/web/src/context';
import { recalcKey } from '@talon/web/src/context/stores/estimateStore';
import { TSessionEstimateItemForm } from '@talon/web/src/types';
import { USD } from '@talon/web/src/utils/currency';
import { useUndoableDelete } from '@talon/web/src/utils/useUndoableDelete';
import { Box, Text } from 'grommet';
import { capitalize } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef, useState } from 'react';
import { TItemDefaultField } from '@shared/types';
import { EditedMarker } from '../EditedMarker';
import { Recalculating } from '../Recalculating';

export interface ISessionEstimateDataRowProps {
  sessionEstimateItem: SessionEstimateItem;
  index: number;
  useColors?: boolean;
  isUnion?: boolean;
}

export const SessionEstimateDataRow = observer(
  ({ sessionEstimateItem, index, useColors, isUnion }: ISessionEstimateDataRowProps) => {
    /** Refs **/
    const defaultFormData = useRef<TSessionEstimateItemForm>({
      title: 'New Estimate Item',
      units: '1',
      unitType: SessionEstimateItemUnitType.Count,
      rate: '0',
      taxesMultiplier: '0',
      handlingMultiplier: '0',
      pAndHMultiplier: '0',
    });

    /** Context **/
    const estimateStore = useEstimateStore();

    // Numbers being recalculated: the whole table's (a new date or contract), or this row's after a save
    const rowsBusy = estimateStore.isRecalculating(
      recalcKey.rows('session', sessionEstimateItem.session_estimate_id ?? '')
    );
    const totalsBusy = rowsBusy || estimateStore.isRecalculating(recalcKey.item(sessionEstimateItem.id));

    /** State **/
    // Hidden while its Undo toast is up; deleted for real when the toast goes
    const [hidden, setHidden] = useState(false);
    const deleteWithUndo = useUndoableDelete();
    const [formData, setFormData] = useState<TSessionEstimateItemForm>({
      title: sessionEstimateItem.title,
      units: sessionEstimateItem.units?.toString() ?? defaultFormData.current.units,
      unitType: sessionEstimateItem.unit_type ?? (defaultFormData.current.unit_type as SessionEstimateItemUnitType),
      rate: sessionEstimateItem.rate?.toString() ?? defaultFormData.current.rate,
      taxesMultiplier: sessionEstimateItem.taxes_multiplier
        ? (sessionEstimateItem.taxes_multiplier * 100).toString()
        : defaultFormData.current.taxesMultiplier,
      handlingMultiplier: sessionEstimateItem.handling_multiplier
        ? (sessionEstimateItem.handling_multiplier * 100).toString()
        : defaultFormData.current.handlingMultiplier,
      pAndHMultiplier: sessionEstimateItem.p_and_h_multiplier
        ? (sessionEstimateItem.p_and_h_multiplier * 100).toString()
        : defaultFormData.current.pAndHMultiplier,
    });

    /** Effects **/

    // Regenerating or resetting changes these without this row saving them
    useEffect(() => {
      setFormData((data) => ({
        ...data,
        rate: sessionEstimateItem.rate?.toString() ?? defaultFormData.current.rate,
        pAndHMultiplier: ((sessionEstimateItem.p_and_h_multiplier ?? 0) * 100).toString(),
        handlingMultiplier: ((sessionEstimateItem.handling_multiplier ?? 0) * 100).toString(),
        taxesMultiplier: ((sessionEstimateItem.taxes_multiplier ?? 0) * 100).toString(),
      }));
    }, [
      sessionEstimateItem.rate,
      sessionEstimateItem.p_and_h_multiplier,
      sessionEstimateItem.handling_multiplier,
      sessionEstimateItem.taxes_multiplier,
    ]);

    /** Computed **/
    const canEdit = estimateStore.canEditCurrent;

    /** Methods **/
    const updateEstimateItem = async (data: TSessionEstimateItemForm) => {
      const { title, units, unitType, rate, taxesMultiplier, handlingMultiplier, pAndHMultiplier } = data;

      await estimateStore.updateSessionEstimateItem(
        sessionEstimateItem.id,
        title,
        parseFloat(units || '0'),
        unitType as SessionEstimateItemUnitType,
        parseFloat(rate || '0'),
        parseFloat(taxesMultiplier || '0') / 100,
        parseFloat(handlingMultiplier || '0') / 100,
        parseFloat(pAndHMultiplier || '0') / 100
      );
    };

    // Set a value edited by hand back to the contract's
    const resetField = (field: TItemDefaultField) =>
      estimateStore.updateSessionEstimateItem(
        sessionEstimateItem.id,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        { resetToDefault: [field as unknown as TResetField] }
      );

    // The dot beside a value edited by hand
    const editedMarker = (field: TItemDefaultField) => (
      <EditedMarker item={sessionEstimateItem} field={field} onReset={canEdit ? () => resetField(field) : undefined} />
    );

    const deleteEstimateItem = () =>
      deleteWithUndo({
        title: sessionEstimateItem.title,
        hide: () => setHidden(true),
        restore: () => setHidden(false),
        commit: () => estimateStore.deleteSessionEstimateItem(sessionEstimateItem.id),
      });

    /** Render **/
    if (hidden) return null;
    return (
      <Box>
        <Box
          className="line-item"
          direction="row"
          gap="medium"
          pad="medium"
          align="center"
          background={useColors && !(index % 2) ? 'light-1' : 'white'}
          // Numbers line up in their right-aligned columns
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          <Box fill="horizontal">
            {canEdit ? (
              <EditableText
                val={formData.title}
                onChange={(title) => setFormData({ ...formData, title })}
                // The title just typed: saved straight away (Enter), its state update hasn't happened yet
                onBlur={(typed?: string) => updateEstimateItem({ ...formData, title: typed ?? formData.title })}
                onSubmit={(typed?: string) => updateEstimateItem({ ...formData, title: typed ?? formData.title })}
              />
            ) : (
              <Text>{formData.title}</Text>
            )}
          </Box>

          <Box width="75%" direction="row" gap="small" align="center" justify="end">
            <Box>
              {canEdit ? (
                <EditableText
                  val={formData.units}
                  onChange={(units) => {
                    const data = { ...formData, units };
                    setFormData(data);
                    updateEstimateItem(data);
                  }}
                  onBlur={() => updateEstimateItem(formData)}
                  onSubmit={() => updateEstimateItem(formData)}
                  minValue={1}
                  inputType="number"
                />
              ) : (
                <Text>{formData.units}</Text>
              )}
            </Box>
            <Box>
              {canEdit ? (
                <SelectableText
                  val={formData.unitType}
                  onChange={(unitType) => {
                    const data = {
                      ...formData,
                      unitType: unitType as SessionEstimateItemUnitType,
                    };
                    setFormData(data);
                    updateEstimateItem(data);
                  }}
                  onBlur={() => updateEstimateItem(formData)}
                  onSubmit={() => updateEstimateItem(formData)}
                  options={Object.values(SessionEstimateItemUnitType).map((unitType) => ({
                    label: capitalize(unitType),
                    value: unitType,
                  }))}
                  capitalize
                />
              ) : (
                <Text>{capitalize(formData.unitType)}</Text>
              )}
            </Box>
          </Box>
          <Box width="55%" direction="row" align="center" justify="end" gap="xsmall">
            {editedMarker('rate')}
            <Recalculating busy={rowsBusy}>
              {canEdit ? (
                <EditableText
                  val={formData.rate}
                  onChange={(rate) => {
                    const data = { ...formData, rate };
                    setFormData(data);
                    updateEstimateItem(data);
                  }}
                  onBlur={() => updateEstimateItem(formData)}
                  onSubmit={() => updateEstimateItem(formData)}
                  inputType="number"
                  isCurrency
                />
              ) : (
                <Text>{USD(formData.rate)}</Text>
              )}
            </Recalculating>
          </Box>
          <Box width="60%" align="end">
            <Recalculating busy={totalsBusy}>
              <Text weight={600}>{USD(sessionEstimateItem?.subtotal)}</Text>
            </Recalculating>
          </Box>
          <Box width="40%" direction="row" align="center" justify="end" gap="xsmall">
            {isUnion && editedMarker('p_and_h_multiplier')}
            <Recalculating busy={rowsBusy}>
              {isUnion &&
                (canEdit ? (
                  <EditableText
                    val={formData.pAndHMultiplier}
                    onChange={(pAndHMultiplier) => {
                      const data = { ...formData, pAndHMultiplier };
                      setFormData(data);
                      updateEstimateItem(data);
                    }}
                    onBlur={() => updateEstimateItem(formData)}
                    onSubmit={() => updateEstimateItem(formData)}
                    inputType="number"
                    isPercent
                  />
                ) : (
                  <Text>{formData.pAndHMultiplier}%</Text>
                ))}
              {!isUnion && <Text weight={300}>—</Text>}
            </Recalculating>
          </Box>
          <Box width="40%" direction="row" align="center" justify="end" gap="xsmall">
            {editedMarker('handling_multiplier')}
            <Recalculating busy={rowsBusy}>
              {canEdit ? (
                <EditableText
                  val={formData.handlingMultiplier}
                  onChange={(handlingMultiplier) => {
                    const data = { ...formData, handlingMultiplier };
                    setFormData(data);
                    updateEstimateItem(data);
                  }}
                  onBlur={() => updateEstimateItem(formData)}
                  onSubmit={() => updateEstimateItem(formData)}
                  inputType="number"
                  isPercent
                />
              ) : (
                <Text>{formData.handlingMultiplier}%</Text>
              )}
            </Recalculating>
          </Box>
          <Box width="40%" direction="row" align="center" justify="end" gap="xsmall">
            {editedMarker('taxes_multiplier')}
            <Recalculating busy={rowsBusy}>
              {canEdit ? (
                <EditableText
                  val={formData.taxesMultiplier}
                  onChange={(taxesMultiplier) => {
                    const data = { ...formData, taxesMultiplier };
                    setFormData(data);
                    updateEstimateItem(data);
                  }}
                  onBlur={() => updateEstimateItem(formData)}
                  onSubmit={() => updateEstimateItem(formData)}
                  inputType="number"
                  isPercent
                />
              ) : (
                <Text>{formData.taxesMultiplier}%</Text>
              )}
            </Recalculating>
          </Box>
          <Box width="70%" align="end">
            <Recalculating busy={totalsBusy}>
              <Text weight={600}>{USD(sessionEstimateItem?.subtotal_plus_fringe)}</Text>
            </Recalculating>
          </Box>
          {canEdit ? (
            <LineItemDelete name={sessionEstimateItem.title} onClick={deleteEstimateItem} />
          ) : (
            <Box width="40px" flex={false} />
          )}
        </Box>
      </Box>
    );
  }
);
